import React, { useState, useContext } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, runTransaction } from 'firebase/firestore';
import { AuthContext } from '../App';

function MessageBox({ message, onClose, type = 'info' }) {
  if (!message) return null;
  
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
        <p className="text-lg font-semibold text-gray-800 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg shadow-md text-white font-semibold transition-all duration-300 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          OK
        </button>
      </div>
    </div>
  );
}

export default function RegisterPage({ onLoginClick }) {
  const { auth, db } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [loading, setLoading] = useState(false);

  const countries = [
    { code: '+91', name: 'India', flag: '🇮🇳', example: '9876543210' },
    { code: '+94', name: 'Sri Lanka', flag: '🇱🇰', example: '712345678' },
    { code: '+44', name: 'United Kingdom', flag: '🇬🇧', example: '7911123456' },
    { code: '+1', name: 'United States', flag: '🇺🇸', example: '2125551234' },
    { code: '+61', name: 'Australia', flag: '🇦🇺', example: '412345678' },
    { code: '+971', name: 'UAE', flag: '🇦🇪', example: '501234567' }
  ];

  const getExampleNumber = () => {
    const country = countries.find(c => c.code === countryCode);
    return country ? country.example : '1234567890';
  };

  const validatePhoneNumber = (number) => {
    const cleanNumber = number.replace(/\D/g, '');
    
    const lengthRules = {
      '+91': { min: 10, max: 10 },
      '+94': { min: 9, max: 9 },
      '+44': { min: 10, max: 11 },
      '+1': { min: 10, max: 10 },
      '+61': { min: 9, max: 9 },
      '+971': { min: 9, max: 9 }
    };
    
    const rule = lengthRules[countryCode];
    if (rule) {
      return cleanNumber.length >= rule.min && cleanNumber.length <= rule.max;
    }
    return cleanNumber.length >= 7 && cleanNumber.length <= 15;
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const sanitized = value.replace(/[^\d]/g, '');
    setPhoneNumber(sanitized);
  };

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      showMessage('Please fill in all required fields.', 'error');
      setLoading(false);
      return;
    }

    if (!phoneNumber.trim()) {
      showMessage('Please enter your phone number.', 'error');
      setLoading(false);
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      showMessage(`Please enter a valid phone number for ${countryCode}. Example: ${getExampleNumber()}`, 'error');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      showMessage('Passwords do not match.', 'error');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      showMessage('Password must be at least 6 characters long.', 'error');
      setLoading(false);
      return;
    }

    try {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  const cleanNumber = phoneNumber.replace(/\D/g, '');
  const fullPhoneNumber = `${countryCode}${cleanNumber}`;
  
  // Generate unique user ID with transaction
  const counterRef = doc(db, 'counters', 'userId');
  
  let uniqueUserId = '';
  
  try {
    const nextNumber = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let newCount = 1;
      if (counterDoc.exists()) {
        newCount = (counterDoc.data().count || 0) + 1;
      }
      
      transaction.set(counterRef, { count: newCount }, { merge: true });
      return newCount;
    });

    // Format the ID as DCM00001, DCM00002, etc.
    uniqueUserId = `DCM${String(nextNumber).padStart(5, '0')}`;
    console.log('Generated unique user ID:', uniqueUserId);
    
  } catch (transactionError) {
    console.error('Transaction error:', transactionError);
    // Fallback: use timestamp-based ID if transaction fails
    uniqueUserId = `DCM${Date.now().toString().slice(-5)}`;
    console.log('Using fallback ID:', uniqueUserId);
  }

// Save user data to Firestore
const userDocRef = doc(db, 'users', user.uid);
await setDoc(userDocRef, {
  email: email,
  firstName: firstName.trim(),
  lastName: lastName.trim(),
  phoneNumber: fullPhoneNumber,
  countryCode: countryCode,
  uniqueUserId: uniqueUserId,  // ← NEW FIELD ADDED
  role: 'user',
  isProfileApproved: false,
  phoneVerified: false,
  createdAt: new Date(),
}, { merge: true });

showMessage('Registration successful! Your account has been created.', 'success');
      setTimeout(() => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setMessage('');
      }, 2000);

    } catch (error) {
      console.error("Registration error:", error);
      
      let errorMessage = 'Registration failed. ';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage += 'Email is already registered. Please use a different email.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage += 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage += 'Invalid email format. Please check your email.';
      } else {
        errorMessage += error.message;
      }
      
      showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4" style={{ background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)' }}>
      <div className="p-10 rounded-3xl w-full max-w-md" style={{ backdropFilter: 'blur(20px)', background: 'rgba(255, 255, 255, 0.95)', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)' }}>
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
            Register
          </h2>
          <p className="text-gray-600">
            Create your account to begin your divine journey
          </p>
        </div>
        
        <form onSubmit={handleRegister} className="space-y-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              placeholder="Your First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          
          <div>
            <label htmlFor="lastName" className="block text-sm font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              placeholder="Your Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              Email *
            </label>
            <input
              type="email"
              id="email"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              placeholder="your@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              Phone Number *
            </label>
            
            <div className="mb-2">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none bg-white"
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name} ({country.code})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <input
                type="tel"
                id="phoneNumber"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
                placeholder={getExampleNumber()}
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                required
                disabled={loading}
                inputMode="numeric"
              />
              <p className="mt-1 text-xs text-gray-500">
                Example: {getExampleNumber()}
              </p>
            </div>
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              Password *
            </label>
            <input
              type="password"
              id="password"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength="6"
              disabled={loading}
            />
          </div>
          
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-semibold mb-2" style={{ color: 'hsl(221, 45%, 18%)' }}>
              Confirm Password *
            </label>
            <input
              type="password"
              id="confirmPassword"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength="6"
              disabled={loading}
            />
          </div>
          
          <button
            type="submit"
            className="w-full py-3 rounded-xl text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
            disabled={loading}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        
        <p className="mt-8 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <button
            onClick={onLoginClick}
            className="font-semibold hover:underline transition duration-200"
            style={{ color: 'hsl(43, 74%, 52%)' }}
          >
            Sign In
          </button>
        </p>
        
        <MessageBox 
          message={message} 
          onClose={() => setMessage('')} 
          type={messageType}
        />
      </div>
    </div>
  );
}